import type { ReactNode } from 'react';

// @-mentions are a display-only convenience: no notification, no storage change.
// A message body is plain text; we simply highlight any "@Name" that matches a
// known player when rendering. A mention only starts at an '@' at the start of the
// text or just after whitespace (same boundary as activeMention below), so
// "email me@Bob" is left alone. Names can contain spaces (e.g. "Iain M"), so at
// each such '@' we greedily match the LONGEST known name, case-insensitively, and
// only when a word boundary follows — so "@Iain" won't wrongly swallow "@Iain W".
// This MUST stay in lockstep with the backend (api/src/shared/mentions.js) so the
// unread-mention badge counts exactly what is highlighted here.

/** Split `body` into text and highlighted-mention spans against `names`. */
export function renderWithMentions(body: string, names: string[]): ReactNode {
  if (!names.length || body.indexOf('@') === -1) return body;
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const out: ReactNode[] = [];
  let buf = '';
  let i = 0;
  let key = 0;
  const flush = () => {
    if (buf) {
      out.push(buf);
      buf = '';
    }
  };
  while (i < body.length) {
    if (body[i] === '@' && (i === 0 || /\s/.test(body[i - 1]))) {
      const rest = body.slice(i + 1);
      const restLower = rest.toLowerCase();
      let hit: string | null = null;
      for (const n of sorted) {
        if (restLower.startsWith(n.toLowerCase())) {
          const after = rest[n.length];
          if (after === undefined || !/[a-z0-9]/i.test(after)) {
            hit = n;
            break;
          }
        }
      }
      if (hit) {
        flush();
        // Keep the caser the user typed, but render off the matched length.
        out.push(
          <span className="mention" key={`m${key++}`}>
            @{rest.slice(0, hit.length)}
          </span>,
        );
        i += 1 + hit.length;
        continue;
      }
    }
    buf += body[i];
    i++;
  }
  flush();
  return out;
}

/**
 * Find the @-mention being typed immediately before the caret, if any. A mention
 * starts at an '@' that sits at the start of the text or just after whitespace;
 * the query runs from there to the caret (single line, reasonably short).
 */
export function activeMention(text: string, caret: number): { at: number; query: string } | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;
  const before = at === 0 ? '' : upto[at - 1];
  if (before && !/\s/.test(before)) return null;
  const query = upto.slice(at + 1);
  if (query.includes('\n') || query.length > 24) return null;
  return { at, query };
}
